<template>
  <div>
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/admin/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>系统管理</el-breadcrumb-item>
      <el-breadcrumb-item>日志管理</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card>
      <div class="search-wrapper">
        <div class="search-toggle" v-show="isMobile" @click="toggleSearch">
          <i :class="isSearchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          <span>{{ isSearchExpanded ? '收起筛选' : '展开筛选' }}</span>
        </div>
        <div class="search-content" :class="{ 'search-content-mobile': isMobile }" v-show="!isMobile || isSearchExpanded">
          <div class="toolbar">
            <div class="toolbar-left">
              <el-select
                v-model="filters.logType"
                placeholder="日志类型"
                clearable
                size="small"
                @change="handleLogTypeChange"
              >
                <el-option
                  v-for="option in logTypeOptions"
                  :key="option.value || 'all'"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
              <el-button
                type="primary"
                size="small"
                icon="el-icon-refresh"
                @click="fetchLogFiles"
              >
                刷新
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <div class="table-scroll-wrapper">
        <el-table
          :data="logFiles"
          border
          stripe
          size="small"
        >
        <el-table-column type="index" label="#" width="60" align="center" />
        <el-table-column label="文件名" prop="name"/>
        <el-table-column label="类型" width="120" align="center">
          <template v-slot="scope">
            <el-tag :type="logTypeTagType(scope.row.type)" effect="plain">
              {{ formatLogType(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="大小" width="130" align="center">
          <template v-slot="scope">
            {{ formatSize(scope.row.size_bytes) }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="180" align="center">
          <template v-slot="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="最后修改" width="180" align="center">
          <template v-slot="scope">
            {{ formatDate(scope.row.modified_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="270" align="center">
          <template v-slot="scope">

            <el-button
              type="primary"
              icon="el-icon-view"
              size="mini"
              plain
              @click="openViewDialog(scope.row)"
            />


            <el-button
              type="success"
              icon="el-icon-download"
              size="mini"
              plain
              @click="handleDownload(scope.row)"
            />


            <el-button
              type="warning"
              icon="el-icon-edit"
              size="mini"
              plain
              @click="openEditDialog(scope.row)"
            />


            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              plain
              @click="handleDelete(scope.row)"
            />

          </template>
        </el-table-column>
      </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          @size-change="onSizeChange"
          @current-change="onPageChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          :layout="isMobile ? 'total, sizes, prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
          :total="total"
        />
      </div>
    </el-card>

    <el-dialog
      title="查看日志"
      :visible.sync="viewDialog.visible"
      width="900px"
      top="10vh"
      :close-on-click-modal="false"
      @closed="resetViewDialog"
    >
      <div class="view-header">
        <div class="view-title">{{ viewDialog.file?.name || '-' }}</div>
        <div class="view-stats">
            <span class="stat-pill">总行数：{{ viewDialog.totalLines }}</span>
            <span class="stat-pill">展示行数：{{ viewDialog.returnedLines }}</span>
        </div>
        <div class="view-actions">
          <el-input
            v-model="viewDialog.search"
            placeholder="请输入关键字"
            clearable
            size="mini"
            class="search-input"
            @keyup.enter.native="fetchLogContent"
            @clear="fetchLogContent"
          >
            <el-button slot="append" icon="el-icon-search" size="mini" @click="fetchLogContent"></el-button>
          </el-input>
          <div class="view-controls">
            <el-switch
              v-model="viewDialog.fromEnd"
              class="custom-switch"
              active-text="末尾"
              inactive-text="开头"
              @change="fetchLogContent"
            />
            <el-input-number
              v-model="viewDialog.maxLines"
              :min="1"
              :max="2000"
              size="mini"
              @change="fetchLogContent"
            />
          </div>
          <el-button
            type="primary"
            size="mini"
            icon="el-icon-refresh"
            :loading="viewDialog.loading"
            @click="fetchLogContent"
          >
            刷新
          </el-button>
        </div>
      </div>
      <div class="content-shell is-view">
        <el-scrollbar wrap-class="content-wrap" view-class="content-view">
          <pre>{{ viewDialog.content || '（无内容）' }}</pre>
        </el-scrollbar>
      </div>
      <el-alert
        v-if="viewDialog.truncated"
        class="view-tip"
        type="warning"
        show-icon
        title="当前仅展示最近的部分内容，可调整返回行数查看更多。"
      />
    </el-dialog>

    <el-dialog
      title="编辑日志"
      :visible.sync="editDialog.visible"
      width="900px"
      top="10vh"
      :close-on-click-modal="false"
      @closed="resetEditDialog"
    >
      <div class="edit-header">
        <div class="file-info">
            <div class="file-title">{{ editDialog.file?.name || '-' }}</div>
            <div class="file-meta">
                <span class="meta-value">最后修改：{{ formatDate(editDialog.file?.modified_at) }}</span>
                <span class="meta-value">文件总行数：{{ editDialog.totalLines ?? '-' }}</span>
                <span class="meta-value">已加载行数：{{ editDialog.returnedLines ?? 0 }}</span>
            </div>
        </div>
        <div class="edit-controls">
          <el-radio-group
            v-model="editDialog.mode"
            size="small"
            class="edit-mode"
            @change="handleEditModeChange"
          >
            <el-radio-button label="overwrite">覆盖</el-radio-button>
            <el-radio-button label="append">追加</el-radio-button>
          </el-radio-group>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-refresh"
            :loading="editDialog.loadingContent"
            :disabled="editDialog.loadingContent"
            @click="handleReloadEditContent"
          >
            重新拉取
          </el-button>
        </div>
      </div>
      <div class="content-shell is-edit">
        <el-skeleton v-if="editDialog.loadingContent" animated :rows="6" />
        <el-input
          v-else
          v-model="editDialog.content"
          type="textarea"
          class="content-editor"
          :rows="18"
          :placeholder="editPlaceholder"
        />
      </div>
      <template slot="footer">
        <div class="dialog-footer">
          <div class="edit-footer-meta">
            <span v-if="editDialog.mode === 'overwrite'">
              可直接在文本中修改或删除任意内容，提交后将覆盖原文件。
            </span>
            <span v-else>将把文本内容追加到文件末尾，不会改动原有内容。</span>
          </div>
          <div class="dialog-footer-actions">
            <el-button @click="editDialog.visible = false" :disabled="editDialog.loading">
              取消
            </el-button>
            <el-button
              type="primary"
              :loading="editDialog.loading"
              @click="submitEdit"
            >
              提交
            </el-button>
          </div>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'LogManager',
  data() {
    return {
      isSearchExpanded: false,
      isMobile: false,
      logFiles: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      filters: {
        logType: ''
      },
      logTypeOptions: [
        { label: '全部类型', value: '' },
        { label: '应用日志', value: 'app' },
        { label: '错误日志', value: 'error' },
        { label: '其他', value: 'other' }
      ],
      viewDialog: {
        visible: false,
        loading: false,
        file: null,
        content: '',
        totalLines: 0,
        returnedLines: 0,
        truncated: false,
        encoding: '',
        maxLines: 200,
        search: '',
        fromEnd: true
      },
      editDialog: {
        visible: false,
        loading: false,
        loadingContent: false,
        file: null,
        content: '',
        mode: 'overwrite',
        totalLines: null,
        returnedLines: null,
        truncated: false,
        fetchedContent: '',
        lastOverwriteContent: '',
        maxLines: 2000,
        fullLoadRequested: false
      }
    };
  },
  created() {
    this.fetchLogFiles();
    this.checkIsMobile();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  computed: {
    editPlaceholder() {
      if (this.editDialog.mode === 'append') {
        return '请输入要追加的内容，将在日志末尾新增。';
      }
      return '可直接编辑日志内容，提交后将覆盖原文件。支持删除或修改任意段落。';
    }
  },
  methods: {
    fetchLogFiles() {
      this.$http({
        method: 'get',
        url: '/logs',
        params: {
          log_type: this.filters.logType || undefined,
          page: this.pageNum,
          size: this.pageSize
        }
      })
        .then(res => {
          if (!res?.success) {
            return this.$message.error(res?.message || '获取日志列表失败');
          }
          this.logFiles = res.data || [];
          this.total = res.total || 0;
        })
        .catch(err => {
          this.$message.error(err?.response?.data?.message || '获取日志列表失败');
        });
    },
    handleLogTypeChange() {
      this.pageNum = 1;
      this.fetchLogFiles();
    },
    onSizeChange(newSize) {
      this.pageSize = newSize;
      this.pageNum = 1;
      this.fetchLogFiles();
    },
    onPageChange(newPage) {
      this.pageNum = newPage;
      this.fetchLogFiles();
    },
    formatLogType(type) {
      if (!type) return '未知';
      const map = {
        app: '应用日志',
        error: '错误日志',
        other: '其他日志'
      };
      return map[type] || type;
    },
    logTypeTagType(type) {
      switch (type) {
        case 'app':
          return 'success';
        case 'error':
          return 'danger';
        case 'other':
          return 'info';
        default:
          return 'default';
      }
    },
    formatSize(size) {
      if (size === null || size === undefined) return '-';
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    },
    formatDate(value) {
      if (!value) return '-';
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return value;
      const y = dt.getFullYear();
      const m = `${dt.getMonth() + 1}`.padStart(2, '0');
      const d = `${dt.getDate()}`.padStart(2, '0');
      const hh = `${dt.getHours()}`.padStart(2, '0');
      const mm = `${dt.getMinutes()}`.padStart(2, '0');
      const ss = `${dt.getSeconds()}`.padStart(2, '0');
      return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
    },
    openViewDialog(file) {
      this.viewDialog.visible = true;
      this.viewDialog.file = file;
      this.viewDialog.maxLines = 200;
      this.viewDialog.search = '';
      this.fetchLogContent();
    },
    fetchLogContent(newMaxLines) {
      if (!this.viewDialog.file) return;
      if (typeof newMaxLines === 'number' && Number.isFinite(newMaxLines)) {
        this.viewDialog.maxLines = newMaxLines;
      }
      this.viewDialog.loading = true;
      this.$http({
        method: 'get',
        url: `/logs/${encodeURIComponent(this.viewDialog.file.name)}`,
        params: {
          max_lines:
            typeof this.viewDialog.maxLines === 'number' && this.viewDialog.maxLines > 0
              ? this.viewDialog.maxLines
              : undefined,
          search: this.viewDialog.search || undefined,
          from_end: this.viewDialog.fromEnd
        }
      })
        .then(res => {
          if (!res?.success) {
            this.viewDialog.loading = false;
            return this.$message.error(res?.message || '获取日志内容失败');
          }
          const data = res.data || {};
          this.viewDialog.loading = false;
          this.viewDialog.content = data.content || '';
          this.viewDialog.totalLines =
            typeof data.total_lines === 'number' ? data.total_lines : 0;
          this.viewDialog.returnedLines =
            typeof data.returned_lines === 'number' ? data.returned_lines : 0;
          this.viewDialog.truncated = Boolean(data.truncated);
          this.viewDialog.encoding = data.encoding || '';
        })
        .catch(err => {
          this.viewDialog.loading = false;
          this.$message.error(err?.response?.data?.message || '获取日志内容失败');
        });
    },
    resetViewDialog() {
      this.viewDialog = {
        visible: false,
        loading: false,
        file: null,
        content: '',
        totalLines: 0,
        returnedLines: 0,
        truncated: false,
        encoding: '',
        maxLines: 200,
        search: '',
        fromEnd: true
      };
    },
    handleDownload(file) {
      this.$http({
        method: 'get',
        url: `/logs/${encodeURIComponent(file.name)}/download`,
        responseType: 'blob'
      })
        .then(blob => {
          if (blob.type && blob.type.includes('application/json')) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const result = JSON.parse(reader.result);
                this.$message.error(result?.message || '下载失败');
              } catch (error) {
                this.$message.error('下载失败');
              }
            };
            reader.readAsText(blob);
            return;
          }
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(err => {
          this.$message.error(err?.response?.data?.message || '下载失败');
        });
    },
    openEditDialog(file) {
      this.editDialog.visible = true;
      this.editDialog.file = file;
      this.editDialog.content = '';
      this.editDialog.mode = 'overwrite';
      this.editDialog.loadingContent = false;
      this.editDialog.totalLines = null;
      this.editDialog.returnedLines = null;
      this.editDialog.truncated = false;
      this.editDialog.fetchedContent = '';
      this.editDialog.lastOverwriteContent = '';
      this.editDialog.fullLoadRequested = false;
      this.loadEditContent();
    },
    handleReloadEditContent() {
      this.editDialog.fullLoadRequested = false;
      this.loadEditContent(true);
    },
    loadEditContent(manual = false, overrideMaxLines) {
      if (!this.editDialog.file) return;
      const isRecursiveCall =
        typeof overrideMaxLines === 'number' && this.editDialog.fullLoadRequested;
      if (!isRecursiveCall) {
        this.editDialog.fullLoadRequested = false;
      }

      const proceed = () => {
        this.editDialog.loadingContent = true;
        const appliedMaxLines =
          typeof overrideMaxLines === 'number' && Number.isFinite(overrideMaxLines)
            ? overrideMaxLines
            : this.editDialog.mode === 'append'
              ? this.editDialog.maxLines
              : undefined;
        if (typeof appliedMaxLines === 'number' && Number.isFinite(appliedMaxLines)) {
          this.editDialog.maxLines = appliedMaxLines;
        }
        const params =
          typeof appliedMaxLines === 'number' && appliedMaxLines > 0
            ? {
                max_lines: appliedMaxLines
              }
            : undefined;

        this.$http({
          method: 'get',
          url: `/logs/${encodeURIComponent(this.editDialog.file.name)}`,
          params
        })
          .then(res => {
            if (!res?.success) {
              this.editDialog.loadingContent = false;
              return this.$message.error(res?.message || '获取日志内容失败');
            }
            const data = res.data || {};
            if (
              data.truncated &&
              typeof data.total_lines === 'number' &&
              !this.editDialog.fullLoadRequested
            ) {
              this.editDialog.fullLoadRequested = true;
              this.editDialog.loadingContent = false;
              this.loadEditContent(manual, data.total_lines);
              return;
            }
            this.editDialog.fullLoadRequested = Boolean(data.truncated);
            this.editDialog.loadingContent = false;
            this.editDialog.fetchedContent = data.content || '';
            this.editDialog.totalLines = data.total_lines ?? null;
            this.editDialog.returnedLines = data.returned_lines ?? null;
            this.editDialog.truncated = Boolean(data.truncated);
            this.editDialog.lastOverwriteContent = this.editDialog.fetchedContent;
            if (this.editDialog.mode === 'overwrite') {
              this.editDialog.content = this.editDialog.fetchedContent;
            }
            if (this.editDialog.mode === 'append') {
              this.editDialog.content = '';
            }
            if (typeof data.total_lines === 'number') {
              this.editDialog.maxLines = data.total_lines;
            }
          })
          .catch(err => {
            this.editDialog.fullLoadRequested = false;
            this.editDialog.loadingContent = false;
            this.$message.error(err?.response?.data?.message || '获取日志内容失败');
          });
      };

      if (
        manual &&
        (
          (this.editDialog.mode === 'overwrite' && this.editDialog.content && this.editDialog.content !== this.editDialog.lastOverwriteContent) ||
          (this.editDialog.mode === 'append' && this.editDialog.content)
        )
      ) {
        this.$confirm('重新拉取会覆盖当前正在编辑的内容，是否继续？', '提示', {
          type: 'warning',
          confirmButtonText: '继续',
          cancelButtonText: '取消'
        })
          .then(() => {
            proceed();
          })
          .catch(() => {});
        return;
      }

      proceed();
    },
    handleEditModeChange(mode) {
      if (mode === 'append') {
        this.editDialog.lastOverwriteContent = this.editDialog.content || this.editDialog.lastOverwriteContent || this.editDialog.fetchedContent;
        this.editDialog.content = '';
      } else if (mode === 'overwrite') {
        if (!this.editDialog.lastOverwriteContent) {
          this.editDialog.lastOverwriteContent = this.editDialog.fetchedContent || '';
        }
        this.editDialog.content = this.editDialog.lastOverwriteContent || '';
      }
    },
    submitEdit() {
      if (!this.editDialog.file) return;
      if (this.editDialog.mode === 'append' && !this.editDialog.content) {
        this.$message.warning('请填写要追加的内容');
        return;
      }
      this.editDialog.loading = true;
      this.$http({
        method: 'put',
        url: `/logs/${encodeURIComponent(this.editDialog.file.name)}`,
        data: {
          content: this.editDialog.content,
          mode: this.editDialog.mode
        }
      })
        .then(res => {
          this.editDialog.loading = false;
          if (!res?.success) {
            return this.$message.error(res?.message || '编辑日志失败');
          }
          this.$message.success('日志更新成功');
          if (this.editDialog.mode === 'overwrite') {
            this.editDialog.lastOverwriteContent = this.editDialog.content;
          } else {
            this.editDialog.content = '';
          }
          this.editDialog.visible = false;
          this.fetchLogFiles();
          if (this.viewDialog.visible && this.viewDialog.file?.name === this.editDialog.file.name) {
            this.fetchLogContent();
          }
        })
        .catch(err => {
          this.editDialog.loading = false;
          this.$message.error(err?.response?.data?.message || '编辑日志失败');
        });
    },
    resetEditDialog() {
      this.editDialog = {
        visible: false,
        loading: false,
        loadingContent: false,
        file: null,
        content: '',
        mode: 'overwrite',
        totalLines: null,
        returnedLines: null,
        truncated: false,
        fetchedContent: '',
        lastOverwriteContent: '',
        maxLines: 2000,
        fullLoadRequested: false
      };
    },
    handleDelete(file) {
      this.$confirm(`确定要删除日志文件「${file.name}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          return this.$http({
            method: 'delete',
            url: `/logs/${encodeURIComponent(file.name)}`
          });
        })
        .then(res => {
          if (!res?.success) {
            return this.$message.error(res?.message || '删除日志失败');
          }
          if (res.data !== true) {
            this.$message.warning('删除结果未确认，请刷新列表');
          } else {
            this.$message.success('日志删除成功');
          }
          this.fetchLogFiles();
          if (this.viewDialog.visible && this.viewDialog.file?.name === file.name) {
            this.viewDialog.visible = false;
          }
        })
        .catch(err => {
          if (err !== 'cancel') {
            this.$message.error(err?.response?.data?.message || '删除日志失败');
          }
        });
      },

      toggleSearch() {
      this.isSearchExpanded = !this.isSearchExpanded
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    handleResize() {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile && wasMobile) {
        this.isSearchExpanded = false
      }
    }
    }
  }

</script>

<style scoped>
.search-wrapper {
  margin-bottom: 10px;
}

.search-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: color 0.3s;
}

.search-toggle:hover {
  color: #409eff;
}

.search-toggle i {
  font-size: 14px;
}

.search-content {
  padding-top: 10px;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper .el-table {
  min-width: 100%;
}

.pagination-wrapper {
  margin-top: 15px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
}

.pagination-wrapper .el-pagination {
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  padding: 10px 0;
  min-width: max-content;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  flex: 1;
  min-width: 200px;
}

.view-header {
  display: flex;
  justify-content: space-between;
  /* align-items: center; */
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.view-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #303133;
}

.view-stats {
  display: flex;
  gap: 6px;
  padding: 4px;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f4f6fa;
  color: #606266;
  font-size: 10px;
  line-height: 1;
}

.view-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-input {
  width: 180px;
}

.view-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-switch :deep(.el-switch__label span) {
  font-size: 13px !important;
}

.content-shell {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background-color: #f9fafc;
  color: #303133;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.content-shell.is-view,
.content-shell.is-edit {
  padding: 0;
}

.content-shell.is-edit {
  margin-top: 12px;
  padding: 0  0 0 16px;
  overflow: auto;
}

.content-shell.is-view:hover {
  border-color: #dcdfe6;
  box-shadow: 0 4px 12px rgba(31, 61, 102, 0.08);
}

.content-shell.is-edit:hover {
  border-color: #dcdfe6;
  box-shadow: 0 4px 12px rgba(31, 61, 102, 0.08);
}

.content-shell.is-view ::v-deep .content-wrap {
  max-height: 360px;
  padding: 16px;
}

.content-shell.is-view ::v-deep .content-view {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  font-size: 13px;
  line-height: 1.7;
}

.content-shell.is-edit ::v-deep .el-skeleton {
  width: 100%;
}

.content-shell.is-edit ::v-deep .el-skeleton__item {
  width: 100%;
}

.content-shell.is-view ::v-deep pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.edit-alert {
  margin-bottom: 12px;
}

.view-tip {
  margin-top: 12px;
}

.edit-header {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.file-info {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.file-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #303133;
  word-break: break-all;
}

.file-meta {
  display: flex;
  gap: 6px;
  padding: 6px;
}

.meta-value {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f4f6fa;
  color: #606266;
  font-size: 10px;
  line-height: 1;
}

.edit-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.edit-mode {
  margin-bottom: 0;
}

.content-editor {
  width: 100%;
}

.content-editor ::v-deep .el-textarea {
  width: 100%;
}

.content-editor ::v-deep .el-textarea__inner {
  background-color: transparent;
  color: #303133;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  line-height: 1.7;
  font-size: 13px;
  min-height: 320px;
  border: none;
  box-shadow: none;
  outline: none;
  padding: 0;
  resize: none;
}

.content-editor ::v-deep .el-textarea__inner:focus {
  border: none;
  box-shadow: none;
}

.content-editor ::v-deep .el-textarea__inner::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.content-editor ::v-deep .el-input__count {
  background: transparent;
  color: #909399;
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: -20px;
}

.edit-footer-meta {
  display: flex;
  align-items: center;
  color: #909399;
  font-size: 12px;
}

.dialog-footer-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

@media (max-width: 768px) {
  .el-card {
    margin-top: 10px;
  }

  .table-scroll-wrapper .el-table {
    font-size: 11px;
  }

  .table-scroll-wrapper .el-table th,
  .table-scroll-wrapper .el-table td {
    padding: 8px 5px !important;
  }

  .pagination-wrapper {
    margin-top: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
  }

  .pagination-wrapper .el-pagination {
    font-size: 10px;
    justify-content: flex-start;
    min-width: max-content;
  }

  .pagination-wrapper .btn-prev,
  .pagination-wrapper .btn-next {
    width: 26px;
    height: 26px;
    line-height: 26px;
    font-size: 11px;
    padding: 0;
  }

  .pagination-wrapper .el-pager li {
    padding: 0 2px;
    min-width: 22px;
    height: 26px;
    line-height: 26px;
    font-size: 10px;
  }

  .pagination-wrapper .el-pagination__total {
    font-size: 10px;
    margin-right: 8px;
  }

  .pagination-wrapper .el-pagination__sizes {
    margin-right: 8px;
  }

  .pagination-wrapper .el-pagination__sizes .el-input {
    width: 60px;
  }

  .pagination-wrapper .el-pagination__sizes .el-input__inner {
    height: 24px;
    font-size: 10px;
    padding: 0 5px;
  }

  .search-toggle {
    padding: 10px 0;
    font-size: 13px;
  }

  .search-content-mobile .toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
    top: 5vh;
  }

  .view-header,
  .edit-header {
    flex-direction: row;
    align-items: flex-start;
  }

  .search-input {
    width: 100% !important;
  }

  .content-shell.is-view ::v-deep .content-wrap {
    max-height: 250px;
    padding: 10px;
  }

  .content-shell.is-view ::v-deep .content-view {
    font-size: 11px;
    line-height: 1.5;
  }
}
</style>

